import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { FeatureFlag } from '@ps/web-core-enums';
import type { Currency } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { getWindow } from '@ps/web-core-utils';
import { isPast } from 'date-fns';
import { isEmpty, set } from 'lodash';
import { useCallback, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Subject, lastValueFrom, timer } from 'rxjs';
import { filter, share, take, takeLast, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { JumpDirection } from '@swf/components/StarsWizard/components/Navigation/Navigation';
import { subscribe } from '@swf/core/api/subscribe';
import { CRMZone } from '@swf/core/enums';
import type { ObservableRequest } from '@swf/core/hooks/useRequest';
import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch, RootState, Store } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { ErrorResponse } from '@swf/core/types/Response';
import type { WizardContext } from '@swf/core/types/Wizard';
import { decodeQuery, unflattenObject } from '@swf/core/utils';
import { getCountryCode } from '@swf/core/utils/phoneVerification';

import useCRMBonusCode from '../../hooks/useCRMBonusCode';
import useSetMitIdData from '../../hooks/useSetMitIdData';
import type {
  AvailablePaySystems,
  BonusInfo,
  DepositLimit,
  DepositPayload,
  DepositResult,
  EnvoyBankInfo,
  GooglePayNativeResultError,
  PaySystemDetails,
  PaySystemDetailsResponse,
  PendingCashoutOption,
  RatesInfo,
  SensitiveDepositPayload,
  SingleSavedCard,
  TempAccountDepositStatus,
  TransactionInfo
} from '../../types/Cashier';
import { CashierFlowType, DepositLimitStatus, ProgressBarSteps } from '../../types/Cashier';
import { CardType } from '../../types/cardType';
import {
  CancelledCashoutStatus,
  DepositStatus,
  PaySystemErrorCode,
  TransactionStatus
} from '../../types/status';
import { browserExtraInfo } from '../../utils/browserExtraInfo';
import {
  dollarsToCents,
  filterPaySystems,
  formatExpiryDate,
  getCurrencyRate,
  getDepositStatus,
  getPaymentVerificationPath,
  getSavedMethod,
  trimAsterisks,
  trimWithoutRounding,
  updateFormFieldsValues,
  verifyEmail
} from '../../utils/cashier';
import { checkGooglePayAvailability } from '../../utils/googlePay';
import { handleEffectResponse } from '../../utils/handleEffectResponse';

import { noInputSaved } from './data';

const requestTimeout = 30000;

export const pciPaymentMethods = [
  CardType.Visa,
  CardType.MasterCard,
  CardType.Solo,
  CardType.GamersCard,
  CardType.Todito,
  CardType.Bancontact
];

const submitDeposit = async (
  request: ObservableRequest,
  payload: DepositPayload,
  amount: number
) => {
  const timeout = 60000 * 5;
  const subject = new Subject();
  const depositResponseSubscription = subscribe('/user/queue/cashier').pipe(
    takeUntil(subject),
    share()
  );
  // We return the first response if it is not PARTIALLY_COMPLETED_CHECK,
  // otherwise we wait for 30s and if nothing else is received, return the PARTIALLY_COMPLETED_CHECK
  const firstResponse = lastValueFrom(
    depositResponseSubscription.pipe(
      filter(
        ({ body: { status }, headers: { type } }) =>
          status !== DepositStatus.PARTIALLY_COMPLETED_CHECK &&
          type !== 'notification.wc3.cashier.queued.transactionResult'
      ),

      take(1)
    )
  );
  const lastResponse = lastValueFrom(
    depositResponseSubscription.pipe(takeUntil(timer(30000)), takeLast(1))
  );

  const { body } = await lastValueFrom(
    request<DepositResult>({
      timeout,
      service: 'wc3.deposit.process',
      body: {
        ...payload,
        ...browserExtraInfo,
        amount: dollarsToCents(trimWithoutRounding(amount))
      }
    })
  );

  if (body.status !== DepositStatus.ACKNOWLEDGED) {
    subject.complete();
    return Promise.resolve({ body });
  }

  return Promise.race([firstResponse, lastResponse]).then(({ body }) => {
    subject.complete();
    return Promise.resolve({ body });
  });
};

const submitDepositWC2 = async (
  request: ObservableRequest,
  payload: DepositPayload,
  amount: number,
  wc2Domain: string,
  store: Store
) => {
  const { token, signature, userWebId } = select.session.getLegacySession(store.getState());
  const { body } = await lastValueFrom(
    request<DepositResult>({
      timeout: requestTimeout,
      http: true,
      method: 'POST',
      service: 'wc2/rest/deposit/process?appType=StarsWebCashier',
      httpDomain: wc2Domain,
      headers: {
        authorization: `PsToken2 ${window.btoa(
          [window.btoa(userWebId || ''), token, signature].join(':')
        )}`
      },
      body: {
        ...payload,
        ...browserExtraInfo,
        amount: dollarsToCents(trimWithoutRounding(amount))
      }
    })
  );
  return Promise.resolve({ body });
};

const useDepositServicesData = () => {
  const [query] = Object.keys(useSelector(select.location.getQuery));
  const rawQueryParams = useSelector(select.location.getQuery);
  const wc2Domain = useSelector(select.location.getWC2Domain);
  const isReloader = useSelector(select.account.isReloader);
  const license = useSelector(select.account.getLicense);
  const isRMOK = useSelector(select.account.isRealMoneyOK);
  const ageVerified = useSelector(select.account.isAgeVerified);
  const authVerified = useSelector(select.account.isAuthVerified);
  const isWithdrawalCancellationRestricted = useSelector(
    select.account.isWithdrawalCancellationRestricted
  );
  const isEmbedded = useSelector(select.device.isEmbedded);
  const site = useSelector(select.cms.getSiteId);
  const store = useStore<RootState>() as Store;
  const isAndroid = useSelector(select.device.isAndroid);
  const isIos = useSelector(select.device.isIos);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const isRomanianTemporaryAccount = useSelector(select.account.isRomanianTemporaryAccount);
  const currencyRef = useRef<Currency>(undefined);
  const defaultCountryCurrencyRef = useRef<Currency>(undefined);
  const setMitIdData = useSetMitIdData();

  const request = useRequest();
  const setCRMBonus = useCRMBonusCode(CRMZone.SWC_WELCOME_BONUS);
  const disablePendingWithdrawalCancellation = useFeature(
    FeatureFlag.DisablePendingWithdrawalCancellation
  );

  const isSwcMethodVerificationEnabled = useFeature(FeatureFlag.SwcMethodVerificationDeposit);
  const isSwcWelcomeBonusScreenEnabled = useFeature(FeatureFlag.SwcWelcomeBonusScreen);

  currencyRef.current = useSelector(select.account.getCurrency);
  defaultCountryCurrencyRef.current = useSelector(select.account.getDefaultCurrencyForCountry);

  const {
    account: { updateAccount }
  } = useDispatch<Dispatch>();

  const processBonusCode = async ({ values }: { values: { deposit: { bonusCode: string } } }) => {
    const bonusCode =
      isMobileEmbedded && !isReloader && !isSwcWelcomeBonusScreenEnabled
        ? getWindow()?.starsweb?.payload?.bonusCode
        : values.deposit?.bonusCode;

    try {
      const response = (await lastValueFrom(
        request({
          service: 'wc3.bonus.getCodeInfo',
          body: { bonusCode }
        })
      )) as { body: BonusInfo };

      return Promise.resolve({ bonusInfo: response.body });
    } catch {
      return Promise.reject({
        body: {
          globalErrors: [],
          fieldErrors: [
            {
              code: 'TXTSRV_Please_enter_a_valid_bonus_cod_ELL',
              fieldName: 'deposit.bonusCode'
            }
          ]
        }
      });
    }
  };

  const depositProcessAmountBonusCode = processBonusCode;
  const depositProcessBonusCode = processBonusCode;

  const depositPaySystems = useCallback(
    async ({ values }: WizardContext) => {
      const { current: currency } = currencyRef;
      let bonusCode;
      let payload;

      try {
        const {
          body: { paySystems: availablePaySystems, applePayMerchantId }
        } = await lastValueFrom(
          request<AvailablePaySystems>({
            service: 'wc3.paysystem.get.availablePaySystems',
            body: { currency }
          })
        );

        const hasGooglePay = availablePaySystems.some(
          (paySystem) => paySystem.cardType === CardType.GooglePay
        );
        const isGooglePayInAndroidApp = isEmbedded && isAndroid && hasGooglePay;

        const paySystems = filterPaySystems(
          availablePaySystems,
          applePayMerchantId && isEmbedded
            ? !!window.starsweb?.applepay?.supported
            : 'ApplePaySession' in window && ApplePaySession.canMakePayments(),
          await checkGooglePayAvailability(isGooglePayInAndroidApp, site, currency)
        );

        if (isEmbedded) {
          bonusCode = window.starsweb?.payload?.bonusCode;
        } else {
          bonusCode = values.deposit?.bonusCode;
        }

        if (bonusCode) {
          try {
            const {
              body: { paySystems: bonusReadySystems }
            } = await lastValueFrom(
              request<BonusInfo>({
                service: 'wc3.bonus.getCodeInfo',
                body: { bonusCode }
              })
            );

            set(values, ['deposit', 'bonusCode'], bonusCode);
            payload = {
              paySystems,
              bonusReadySystems
            };
          } catch {
            payload = {
              paySystems,
              bonusReadySystems: []
            };
          }
        }

        return (
          payload || {
            paySystems,
            bonusReadySystems: []
          }
        );
      } catch (error) {
        const {
          body: { message }
        } = error as ErrorResponse;
        return Promise.reject({
          body: {
            globalErrors: [
              {
                code: PaySystemErrorCode.PaySystemsNotAvailable,
                defaultMessage: message
              }
            ]
          }
        });
      }
    },
    [isAndroid, isEmbedded, request, site]
  );

  const depositVerifyEmailCode = async ({ values: { emailVerificationCode } }: WizardContext) =>
    await verifyEmail(emailVerificationCode);

  const depositSavedCards = useCallback(async () => {
    if (!isReloader) {
      return { savedCards: [] };
    }

    const { token, signature, userWebId } = select.session.getLegacySession(store.getState());

    const {
      body: { savedCards }
    } = await lastValueFrom(
      request<{ savedCards: SingleSavedCard[] }>({
        http: true,
        method: 'GET',
        httpDomain: wc2Domain,
        service: '/wc2/rest/account/savedCards',
        headers: {
          authorization: `PsToken2 ${window.btoa(
            [window.btoa(userWebId || ''), token, signature].join(':')
          )}`
        },
        body: { appType: 'StarsWebCashier' }
      })
    );

    return {
      savedCards: savedCards
        .filter((card) => {
          if (card.expMonth && card.expYear) {
            const expDate = new Date(card.expYear, card.expMonth, 0);
            return !isPast(expDate) && card;
          }
          return card;
        })
        .map(({ maskedCardNum, ...rest }) => ({
          savedCard: {
            ...rest,
            maskedCardNum: trimAsterisks(maskedCardNum)
          }
        }))
    };
  }, [isReloader, request, store, wc2Domain]);

  const depositPaySystemDetails = useCallback(
    async ({
      values,
      data: { savedCards, savedCardInfoId, paySystemDetails: wizardPaySystemDetails = {} }
    }: WizardContext) => {
      const { current: currency } = currencyRef;
      const { current: defaultCountryCurrency } = defaultCountryCurrencyRef;
      const {
        deposit: { bonusCode, type, bonusAcknowledged }
      } = values;

      if (savedCards?.length && savedCardInfoId) {
        const [{ savedCard }] = getSavedMethod(savedCards, savedCardInfoId);
        if (savedCard?.hostileBin) {
          return Promise.reject({
            body: { globalErrors: [{ code: PaySystemErrorCode.HostileCard }] }
          });
        }
      }

      if (!isRMOK && license === LicenceId.UK && ageVerified && authVerified) {
        await updateAccount({ realMoneyFlag: true }).catch(() => undefined);
      }

      const {
        body: { paySystemDetails, voucherInfo, asPaybankInfo }
      } = await lastValueFrom(
        request<PaySystemDetailsResponse>({
          service: 'wc3.paysystem.get.paySystemDetails',
          body: { currency, type }
        })
      );

      if (!paySystemDetails.available) {
        return Promise.reject({
          body: {
            globalErrors: [
              {
                defaultMessage: paySystemDetails.msg,
                code:
                  paySystemDetails.type === CardType.WirecardWbt
                    ? PaySystemErrorCode.WbtBankNotAvailable
                    : PaySystemErrorCode.NotAvailable
              }
            ]
          }
        });
      } else {
        if (license === LicenceId.ITALY && paySystemDetails.authenticationRequired) {
          return Promise.reject({
            body: { globalErrors: [{ code: PaySystemErrorCode.AuthenticationRequired }] }
          });
        }

        if (isRomanianTemporaryAccount && !paySystemDetails.remainingLimit) {
          return Promise.reject({
            body: { globalErrors: [{ code: PaySystemErrorCode.TempAccountLimitReached }] }
          });
        }

        if (wizardPaySystemDetails.type) {
          values.deposit = { type };
          if (!isReloader) {
            bonusCode && (values.deposit.bonusCode = bonusCode);
            bonusAcknowledged && (values.deposit.bonusAcknowledged = bonusAcknowledged);
          }
        }

        if (savedCards?.length && savedCardInfoId) {
          const [{ savedCard }] = getSavedMethod(savedCards, savedCardInfoId);
          if (savedCard && noInputSaved.includes(savedCard.paySystem)) {
            values.deposit.savedCardInfoId = savedCardInfoId;
            switch (type) {
              case CardType.OpenBanking:
                values.deposit.productId = savedCard.specificData?.[0]?.bankId;
                break;
              case CardType.PayPal:
                values.deposit.fraudnetMerSessionId = uuidv4().split('-').join('');
                break;
            }
          }
        }

        if (type === CardType.EnvoyLbt) {
          const {
            rates: { curr: envoyBalanceCurrency, rates }
          } = paySystemDetails;

          const envoyCurrencies = rates.map(({ curr }) => curr).sort();
          const defaultEnvoyCurrencies = [defaultCountryCurrency, envoyBalanceCurrency];
          const defaultEnvoyCurrency =
            defaultEnvoyCurrencies.find(
              (defaultCurrency) => defaultCurrency && envoyCurrencies.includes(defaultCurrency)
            ) || envoyCurrencies[0];

          try {
            const { body: getLtbInfoBody } = await lastValueFrom(
              request<EnvoyBankInfo[]>({
                service: 'wc3.cards.getLbtInfo',
                body: { currency: defaultEnvoyCurrency }
              })
            );

            if (getLtbInfoBody.length === 1) {
              [values.deposit.bankInfo] = getLtbInfoBody;
            }

            return { paySystemDetails, envoyBanks: getLtbInfoBody };
          } catch {
            return Promise.reject({
              body: { globalErrors: [{ code: PaySystemErrorCode.NoBankTransfers }] }
            });
          }
        }

        if (type === CardType.PsPayAstropay) {
          const defaultMethod = asPaybankInfo?.find((item) => item.default);
          if (defaultMethod) {
            values.deposit.accountNumber = defaultMethod.bankAccount;
            values.deposit.bankId = defaultMethod.bankId;
          }
        }

        return { paySystemDetails, envoyBanks: [], voucherInfo, asPaybankInfo };
      }
    },
    [
      isReloader,
      isRomanianTemporaryAccount,
      license,
      request,
      isRMOK,
      ageVerified,
      authVerified,
      updateAccount
    ]
  );

  const depositResultContext = () => {
    try {
      if (rawQueryParams && Object.keys(rawQueryParams).length > 1) {
        return Promise.resolve({
          depositResultContext: unflattenObject(rawQueryParams)
        });
      }
      return Promise.resolve({
        depositResultContext: decodeQuery(query)
      });
    } catch {
      return Promise.reject({ body: {} });
    }
  };

  const depositTransactionStatus = useCallback(
    async (context: WizardContext) => {
      const {
        values,
        data: { depositResultContext: depositResultContextData }
      } = context;

      const {
        MD,
        PaRes,
        challenge3DS2MD,
        cres,
        deposit,
        pares,
        previousDeposit,
        redirectStatus,
        securityKey,
        transactionId,
        tx_status: txStatus,
        wc2Paycallback,
        procaction,
        SecurityKey,
        orderID,
        jwt
      } = depositResultContextData;

      if (wc2Paycallback) {
        context.values = {
          ...depositResultContextData
        };

        return Promise.resolve({
          previousDeposit,
          depositResult: {
            wc2Paycallback,
            status: depositResultContextData.status,
            redirectUrl: depositResultContextData.redirect
          }
        });
      }

      if (txStatus) {
        if ([TransactionStatus.APPROVED, TransactionStatus.REJECTED].includes(txStatus)) {
          return Promise.resolve({
            deposit,
            previousDeposit,
            depositResult: {
              status: getDepositStatus(txStatus)
            }
          });
        } else if (!(transactionId || orderID) || !(securityKey || SecurityKey)) {
          return Promise.reject({ body: {} });
        }
      }

      if (
        !transactionId &&
        [CardType.BoletoAstropay, CardType.PsPayAstropay].includes(+deposit?.type)
      ) {
        return Promise.resolve({
          deposit,
          depositResult: {
            status: DepositStatus.UNKNOWN_TRANSACTION_ID
          }
        });
      }

      if (!(transactionId || orderID)) {
        return Promise.resolve({
          depositResult: {
            status: DepositStatus.MISSING_TRANSACTION_ID
          }
        });
      }

      try {
        // TODO: ideally should compose everything from processor into parameter and keep prop name conversion in WMT
        const parameters = {
          ...(cres && { CRes: cres }),
          ...(pares && { PaRes: pares }),
          ...(PaRes && { PaRes }),
          ...(challenge3DS2MD && { MD: challenge3DS2MD.replace(/^md=/i, '') }),
          ...(MD && { MD }),
          ...(procaction && { procaction }),
          ...(jwt && { jwt }),
          ...(transactionId && { orderID: transactionId })
        };

        const {
          body: { status, retry }
        } = await lastValueFrom(
          request<{ status: TransactionStatus; retry: boolean }>({
            service: 'wc3.paycallback.checkWebCashIn',
            body: {
              transactionId: transactionId || orderID,
              securityKey: securityKey || SecurityKey,
              parameters
            }
          })
        );

        if ([CardType.PayPal, CardType.PaySafe].includes(+deposit?.type)) {
          const { body: transactionInfo } = await lastValueFrom(
            request<TransactionInfo>({
              service: 'wc3.transaction.get.transactionInfo',
              body: {
                transactionId: transactionId || orderID
              }
            })
          );

          if (transactionInfo.status === TransactionStatus.APPROVED) {
            depositResultContextData.transactionInfo = transactionInfo;
          }
        }

        context.values = {
          ...values,
          deposit
        };

        let depositResponse: { body: DepositResult } | undefined;

        if (status === TransactionStatus.REJECTED) {
          depositResponse = await lastValueFrom(
            subscribe<{ body: DepositResult }>('/user/queue/cashier').pipe(
              filter(({ body: { status } }) => status === DepositStatus.REJECTED),
              takeUntil(timer(500)),
              take(1)
            ),
            { defaultValue: undefined }
          );
        }

        return Promise.resolve({
          deposit,
          previousDeposit,
          depositResult: depositResponse?.body
            ? { ...depositResponse.body, redirectStatus }
            : {
                redirectStatus,
                retry,
                status: getDepositStatus(status)
              }
        });
      } catch {
        return Promise.reject({ body: {} });
      }
    },
    [request]
  );

  const depositProcess = useCallback(
    async ({ data, values }: WizardContext) => {
      let previousDeposit: DepositPayload;
      let updatedPaySystemDetails: PaySystemDetails;
      const { deposit } = values;

      const {
        paySystemDetails: { rates: paySystemRates },
        googlePayNativeResultError
      } = data;

      const innerDepositProcess = async ({
        rates,
        curr: balanceCurrency
      }: RatesInfo): Promise<{
        deposit: DepositPayload;
        depositResult?: DepositResult;
        paySystemDetails?: PaySystemDetails;
        previousDeposit?: DepositPayload;
        googlePayNativeResultError?: GooglePayNativeResultError;
      }> => {
        const { currency, expiryDate, amount } = deposit;
        const rate = getCurrencyRate(rates, currency);
        const payload: SensitiveDepositPayload = {
          ...deposit,
          balanceCurrency,
          rate,
          upsellId: 0,
          expiryDate: formatExpiryDate(Number(expiryDate))
        };

        if (googlePayNativeResultError) {
          return { deposit: payload, googlePayNativeResultError };
        }

        if (deposit.type === CardType.OpenBanking && isAndroid && isMobileEmbedded) {
          payload.androidPackageName = window.starsweb?.app.packageName;
        }

        if (deposit.type === CardType.OpenBanking && isIos && isMobileEmbedded) {
          payload.iosSchema = window.starsweb?.app.scheme;
        }

        if (deposit.type === CardType.MBWay) {
          payload.accountNumber = `${getCountryCode(deposit.dialingCode)?.dialingCode}##${
            deposit.phoneNumber
          }`;
        }

        const { body } = pciPaymentMethods.includes(deposit.type)
          ? await submitDepositWC2(request, payload, amount, wc2Domain, store)
          : await submitDeposit(request, payload, amount);

        const { status, paySystemDetails, repeatedCashinDeclineOptions } = body;

        if (status === DepositStatus.COMPLETED) {
          data.progressBarStep = ProgressBarSteps.Bonus;
        }

        switch (true) {
          case status === DepositStatus.RATE_CHANGED: {
            const { rates: newRates } = paySystemDetails;
            const newRate = getCurrencyRate(newRates.rates, currency);

            if (newRate > rate) {
              previousDeposit = payload;
              updatedPaySystemDetails = body.paySystemDetails;
              return innerDepositProcess(newRates);
            } else {
              data.depositResult = body;
              data.paySystemDetails = body.paySystemDetails;
              values.deposit = { ...deposit, rate, balanceCurrency };
              return Promise.reject({
                body: { globalErrors: [{ code: PaySystemErrorCode.RateChanged }] }
              });
            }
          }
          case status === DepositStatus.REJECTED && !isEmpty(repeatedCashinDeclineOptions): {
            data.previousDeposit = body;
            return Promise.reject({
              body: { globalErrors: [{ code: PaySystemErrorCode.RepeatedCashinDeclineOptions }] }
            });
          }
          case status === DepositStatus.RMF_REQUIRED: {
            data.userInfoFields = body.realMoneyInformation?.userInfoFields;
            updateFormFieldsValues(values, body.realMoneyInformation?.userInfoFields);
            return Promise.reject({
              body: { globalErrors: [{ code: PaySystemErrorCode.RMFRequired }] }
            });
          }
          case status === DepositStatus.BONUS_ACK_REQUIRED: {
            data.preCashinInfo = body.preCashinInfo;

            return Promise.reject({
              body: { globalErrors: [{ code: PaySystemErrorCode.BonusAcknowledgement }] }
            });
          }
          case status === DepositStatus.REJECTED_BONUS_MIN_DEPOSIT_REQUIRED: {
            return Promise.reject({
              body: {
                globalErrors: [
                  {
                    code: DepositStatus.REJECTED_BONUS_MIN_DEPOSIT_REQUIRED,
                    defaultMessage: body.message.defaultMessage
                  }
                ]
              }
            });
          }
          case status === DepositStatus.CUMULATIVE_CHECK: {
            data.preCashinInfo = body.preCashinInfo;
            return Promise.reject({
              body: { globalErrors: [{ code: PaySystemErrorCode.PepHioAndThirdPartyCheck }] }
            });
          }
          case status === DepositStatus.REJECTED_CARD_NOT_VERIFIED &&
            isSwcMethodVerificationEnabled: {
            return Promise.reject({
              body: { globalErrors: [{ code: PaySystemErrorCode.MethodVerificationRequired }] }
            });
          }
          default: {
            return previousDeposit
              ? {
                  previousDeposit,
                  paySystemDetails: paySystemDetails || updatedPaySystemDetails,
                  deposit: payload,
                  depositResult: body
                }
              : { deposit: payload, depositResult: body };
          }
        }
      };

      return innerDepositProcess(paySystemRates);
    },
    [isAndroid, isIos, isMobileEmbedded, request, wc2Domain, store, isSwcMethodVerificationEnabled]
  );

  const getPendingCashouts = async (): Promise<{ pendingCashouts: PendingCashoutOption[] }> => {
    if (isWithdrawalCancellationRestricted || disablePendingWithdrawalCancellation) {
      return Promise.resolve({ pendingCashouts: [] });
    }
    const { body } = await lastValueFrom(request({ service: 'wc3.cashout.get.pending' }));

    return Promise.resolve({ pendingCashouts: body });
  };

  const cancelPendingCashouts = async ({
    values: {
      password,
      pendingCashouts: { transactions }
    }
  }: WizardContext) => {
    try {
      const { body } = await lastValueFrom(
        request({
          service: 'wc3.cashout.cancel.pending',
          body: {
            password,
            transactions
          }
        })
      );

      if (body.status === CancelledCashoutStatus.NOT_CANCELLABLE) {
        return { cancelledWithdrawResult: { status: CancelledCashoutStatus.NOT_CANCELLABLE } };
      }

      return { cancelledWithdrawResult: body };
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const checkSWFFlow = useCallback(
    ({ data: { rmfPassed } }: WizardContext) => {
      const rootState = store.getState();
      const cashierFlow = select.account.isSWCDeposit(rootState, rmfPassed)
        ? CashierFlowType.FirstDeposit
        : CashierFlowType.WC2Deposit;

      return Promise.resolve({ cashierFlow });
    },
    [store]
  );

  const fetchUserLimits = async (): Promise<{
    limitSet: boolean;
    depositRestricted: boolean;
  }> => {
    const { body: userLimitsInfo } = await lastValueFrom(
      request<DepositLimit[]>({
        service: 'wc3.limit.info'
      })
    );

    if (isEmpty(userLimitsInfo)) {
      return Promise.resolve({ limitSet: false, depositRestricted: false });
    }

    const depositRestricted = userLimitsInfo.every(
      ({ limitStatus }) => limitStatus === DepositLimitStatus.SelfExcluded
    );

    const limitSet =
      license === LicenceId.UK &&
      userLimitsInfo.every(({ limitStatus }) => limitStatus !== DepositLimitStatus.NotSet);

    return Promise.resolve({ limitSet, depositRestricted });
  };

  const depositRetryProcess = useCallback(
    async ({ values }: WizardContext) => {
      const { deposit } = values;
      const { amount } = deposit;

      const innerDepositProcess = async (): Promise<{
        deposit: DepositPayload;
        depositResult?: DepositResult;
        previousDeposit?: DepositPayload;
      }> => {
        const payload: SensitiveDepositPayload = {
          ...deposit,
          ...(!deposit.savedCardInfoId && { accountNumber: '0'.repeat(16) }),
          retry: true,
          upsellId: 0
        };

        const { body } = pciPaymentMethods.includes(deposit.type)
          ? await submitDepositWC2(request, payload, amount, wc2Domain, store)
          : await submitDeposit(request, payload, amount);

        return { deposit: payload, depositResult: body };
      };

      return innerDepositProcess();
    },
    [request, wc2Domain, store]
  );

  const depositPaymentVerificationSave = async (_context: WizardContext) => Promise.resolve({});

  const jumpToPaymentVerification = useCallback(
    async ({ data: { cashierFlow } }: WizardContext) => ({
      goJump: {
        target: getPaymentVerificationPath(cashierFlow),
        direction: JumpDirection.Next
      }
    }),
    []
  );

  const depositOccupationCheck = useCallback(async () => {
    const { body } = await lastValueFrom(
      request<TempAccountDepositStatus>({
        service: '/api/v1-preview/account/getTempAccountDepositStatus',
        method: 'GET'
      })
    );

    if (!body?.availableDepositAmount) return Promise.reject({});

    const { availableDepositAmount } = body;

    return Promise.resolve({ availableDepositAmount });
  }, [request]);

  const depositUpdateOccupation = useCallback(
    async ({ values: { profession } }: Pick<WizardContext, 'values'>) => {
      const response = await updateAccount({
        profession
      });
      return handleEffectResponse(response);
    },
    [updateAccount]
  );

  const depositResultContextMitID = () => setMitIdData;

  const setBackToRmfJumpCreator = useCallback(async ({ data }: Pick<WizardContext, 'data'>) => {
    data.rmfTargetToJump = '#deposit.check-swf-flow';
    return {
      goJump: {
        target: '#rmf.rmf-enable-real-money-flag-dk',
        direction: JumpDirection.Next
      }
    };
  }, []);

  return {
    cancelPendingCashouts,
    depositOccupationCheck,
    checkSWFFlow,
    getPendingCashouts,
    depositPaySystems,
    depositSavedCards,
    depositPaymentVerificationSave,
    depositPaySystemDetails,
    depositProcessAmountBonusCode,
    depositProcessBonusCode,
    depositResultContext,
    depositRetryProcess,
    depositTransactionStatus,
    depositProcess,
    depositUpdateOccupation,
    setCRMBonus,
    fetchUserLimits,
    jumpToPaymentVerification,
    depositResultContextMitID,
    setBackToRmfJumpCreator,
    depositVerifyEmailCode
  };
};

export default useDepositServicesData;
